import React, {
  useState,
  useEffect,
} from "react";

import PropTypes from "prop-types";

import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
  makeStyles,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";

import SaveIcon from "@material-ui/icons/Save";
import SettingsIcon from "@material-ui/icons/Settings";

import Api from "../../../Api";
import api from "../../../axios";

import { useAlert } from "react-alert";
import { selectsetting } from "../../../adminModel/auctions";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  header: {
    padding: theme.spacing(2.25, 2.5),
    backgroundColor: "#fafafa",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
  },

  headerIcon: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eef2ff",
    color: theme.palette.primary.main,
    marginRight: 12,
  },

  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "#1f2937",
  },

  subtitle: {
    marginTop: 3,
    fontSize: 12,
    color: "#6b7280",
  },

  content: {
    padding: theme.spacing(2.5),
  },

  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },

  messageField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },

  helperBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
  },

  helperText: {
    fontSize: 12,
    lineHeight: 1.5,
    color: "#6b7280",
  },

  actionSection: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
  },

  saveButton: {
    minHeight: 42,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: 18,
    paddingRight: 18,
    boxShadow: "none",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const AuctionDetails = ({
  className,
  ...rest
}) => {
  const classes = useStyles();
  const alert = useAlert();

  const [message, setMessage] = useState("");
  const [selectMsg, setSelectMsg] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [getmsg, setGmsg] =
    useState([]);

  /* =====================================================
     LOAD SETTINGS
  ===================================================== */

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const results =
          await selectsetting();

        setGmsg(
          Array.isArray(results)
            ? results
            : []
        );
      } catch (error) {
        console.error(
          "Settings fetch error:",
          error
        );

        alert.error(
          "Failed to load settings."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [alert]);

  /* =====================================================
     HANDLE FIELD CHANGE
  ===================================================== */

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    if (name === "message") {
      setMessage(value);
    } else if (name === "setting") {
      setSelectMsg(value);
    }
  };

  /* =====================================================
     SAVE SETTING
  ===================================================== */

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!selectMsg) {
      alert.error(
        "Please select a setting."
      );
      return;
    }

    try {
      setLoading(true);

      const data = {
        name: selectMsg,
        value: message,
      };

      const config = {
        method: "put",
        maxBodyLength: Infinity,
        url: `${Api.request.URL}/setting`,
        data: data,
      };

      const response =
        await api.request(config);

      alert.success(
        response.data.msg
      );
    } catch (error) {
      console.error(
        "Save setting error:",
        error
      );

      alert.error(
        "Failed to save setting."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className={classes.root}
      {...rest}
    >
      <Grid container spacing={3}>

        <Grid
          item
          xs={12}
          md={8}
          lg={7}
        >
          <form
            onSubmit={
              handleFormSubmit
            }
            className={className}
          >
            <Card
              className={classes.card}
            >

              {/* =====================================
                  HEADER
              ===================================== */}

              <Box
                className={classes.header}
              >
                <Box
                  className={
                    classes.headerRow
                  }
                >

                  <Box
                    className={
                      classes.headerIcon
                    }
                  >
                    <SettingsIcon />
                  </Box>

                  <Box>
                    <Typography
                      className={
                        classes.title
                      }
                    >
                      Settings
                    </Typography>

                    <Typography
                      className={
                        classes.subtitle
                      }
                    >
                      Update website
                      messages and
                      configuration values
                    </Typography>
                  </Box>

                </Box>
              </Box>

              <Divider />

              {/* =====================================
                  FORM
              ===================================== */}

              <CardContent
                className={
                  classes.content
                }
              >

                <TextField
                  fullWidth
                  select
                  name="setting"
                  label="Select Setting"
                  value={selectMsg}
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  className={
                    classes.field
                  }
                  SelectProps={{
                    native: false,
                  }}
                >
                  {getmsg.map(
                    (val, index) => (
                      <option
                        key={index}
                        value={
                          val.name_desc
                        }
                      >
                        {val.name_desc}
                      </option>
                    )
                  )}
                </TextField>

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={10}
                  margin="normal"
                  label="Message"
                  name="message"
                  value={message}
                  onChange={
                    handleChange
                  }
                  variant="outlined"
                  placeholder="Enter message..."
                  className={
                    classes.messageField
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                <Box
                  className={
                    classes.helperBox
                  }
                >
                  <Typography
                    className={
                      classes.helperText
                    }
                  >
                    Select a setting above,
                    then enter the message
                    or value that should be
                    saved for that setting.
                  </Typography>
                </Box>

              </CardContent>

              <Divider />

              {/* =====================================
                  ACTION
              ===================================== */}

              <Box
                className={
                  classes.actionSection
                }
              >

                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  startIcon={
                    <SaveIcon />
                  }
                  className={
                    classes.saveButton
                  }
                  disabled={
                    !selectMsg ||
                    loading
                  }
                >
                  Save Changes
                </Button>

              </Box>

            </Card>
          </form>
        </Grid>

      </Grid>

      {/* =============================================
          LOADING
      ============================================= */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress
          color="inherit"
        />
      </Backdrop>

    </Box>
  );
};

AuctionDetails.propTypes = {
  className: PropTypes.string,
};

export default AuctionDetails;